import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import React from 'react';
import styled from 'styled-components/macro';

interface StyledTooltipProps {
  $backgroundColor?: string;
  $textColor?: string;
  $arrowColor?: string;
  $width?: number | string;
  $theme?: 'light' | 'dark';
}

export interface TooltipProps extends Omit<MuiTooltipProps, 'size'> {
  backgroundColor?: string;
  textColor?: string;
  arrowColor?: string;
  width?: number | string;
  theme?: 'light' | 'dark';
}

const getThemeColors = (theme: 'light' | 'dark' = 'light') => {
  if (theme === 'dark') {
    return {
      primary: '#90caf9',
      secondary: '#f48fb1',
      background: '#121212',
      text: 'rgba(255, 255, 255, 0.87)',
      paper: '#1e1e1e',
      border: 'rgba(255, 255, 255, 0.23)',
      tooltip: 'rgba(97, 97, 97, 0.9)',
      arrow: 'rgba(97, 97, 97, 0.9)',
    };
  }
  return {
    primary: '#1976d2',
    secondary: '#dc004e',
    background: '#ffffff',
    text: 'rgba(0, 0, 0, 0.87)',
    paper: '#ffffff',
    border: 'rgba(0, 0, 0, 0.23)',
    tooltip: 'rgba(97, 97, 97, 0.9)',
    arrow: 'rgba(97, 97, 97, 0.9)',
  };
};

const StyledMuiTooltip = styled(MuiTooltip)<StyledTooltipProps>`
  ${({ $backgroundColor, $theme = 'light' }) => {
    const colors = getThemeColors($theme);
    return $backgroundColor ? `
      .MuiTooltip-tooltip {
        background-color: ${$backgroundColor};
      }
    ` : `
      .MuiTooltip-tooltip {
        background-color: ${colors.tooltip};
      }
    `;
  }}
  ${({ $textColor, $theme = 'light' }) => {
    const colors = getThemeColors($theme);
    return $textColor ? `
      .MuiTooltip-tooltip {
        color: ${$textColor};
      }
    ` : `
      .MuiTooltip-tooltip {
        color: ${colors.text};
      }
    `;
  }}
  ${({ $arrowColor, $theme = 'light' }) => {
    const colors = getThemeColors($theme);
    return $arrowColor ? `
      .MuiTooltip-arrow {
        color: ${$arrowColor};
      }
    ` : `
      .MuiTooltip-arrow {
        color: ${colors.arrow};
      }
    `;
  }}
  ${({ $width }) => $width && `
    .MuiTooltip-tooltip {
      max-width: ${typeof $width === 'number' ? `${$width}px` : $width};
    }
  `}
`;

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  ({
    backgroundColor,
    textColor,
    arrowColor,
    width,
    theme = 'light',
    ...props
  }, ref) => {
    return (
      <StyledMuiTooltip
        {...props}
        $backgroundColor={backgroundColor}
        $textColor={textColor}
        $arrowColor={arrowColor}
        $width={width}
        $theme={theme}
        ref={ref}
      />
    );
  }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip; 